/**
 * Typed factory functions for creating annotations via the new reactive property system.
 * Each function builds a CreateAnnotationParams object with the correct initial properties.
 * This centralizes property identifiers and value types, keeping usePotreeEvents clean.
 */

import { PropertyType, PropertyValueType } from '@pointorama/potree-shared';
import { OpeningKind, StructureKind, STANDARD_WINDOW_TYPE, type DoorGlyphType, type WindowGlyphType } from 'shared-types';
import type { CreateAnnotationParams, InitialProperty } from './useCreateAnnotation';

type Coordinate = { x: number; y: number; z: number };

/** Shared properties included in every annotation creation */
function sharedProperties(id: string, name: string, color: string): InitialProperty[] {
  return [
    {
      identifier: 'annotation_name',
      objectIdentifier: id,
      value: name,
      valueType: PropertyValueType.STRING,
      propertyType: PropertyType.PROJECT_GLOBAL,
    },
    {
      identifier: 'annotation_color',
      objectIdentifier: id,
      value: color,
      valueType: PropertyValueType.COLOR,
      propertyType: PropertyType.PROJECT_GLOBAL,
    },
  ];
}

function prop(id: string, identifier: string, value: unknown, valueType: PropertyValueType): InitialProperty {
  return { identifier, objectIdentifier: id, value, valueType, propertyType: PropertyType.PROJECT_GLOBAL };
}

// ============================================================================
// POINT
// ============================================================================

export function buildCreatePointParams(args: {
  identifier: string;
  position: Coordinate;
  pickColor: string;
  classification: number;
  intensity: number;
  pointCloudId: string;
  name: string;
  color: string;
  groupIdentifier?: string | null;
}): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'POINT',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'point_position', args.position, PropertyValueType.COORDINATE),
      prop(args.identifier, 'point_pick_color', args.pickColor, PropertyValueType.COLOR),
      prop(args.identifier, 'point_classification', args.classification, PropertyValueType.INT),
      prop(args.identifier, 'point_intensity', args.intensity, PropertyValueType.FLOAT),
      prop(args.identifier, 'point_pointcloud_id', args.pointCloudId, PropertyValueType.STRING),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

// ============================================================================
// BOX
// ============================================================================

export function buildCreateBoxParams(args: { identifier: string; position: Coordinate; scale: Coordinate; rotation: Coordinate; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'BOX',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'box_position', args.position, PropertyValueType.COORDINATE),
      prop(args.identifier, 'box_scale', args.scale, PropertyValueType.COORDINATE),
      prop(args.identifier, 'box_rotation', args.rotation, PropertyValueType.COORDINATE),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

// ============================================================================
// DISTANCE
// ============================================================================

type AnnotationPoint = { position: Coordinate; pointCloudId?: string | null };

export function buildCreateDistanceParams(args: { identifier: string; points: AnnotationPoint[]; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'DISTANCE',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [prop(args.identifier, 'distance_points', JSON.stringify(args.points), PropertyValueType.JSON), ...sharedProperties(args.identifier, args.name, args.color)],
  };
}

// ============================================================================
// AREA
// ============================================================================

export function buildCreateAreaParams(args: { identifier: string; points: AnnotationPoint[]; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'AREA',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [prop(args.identifier, 'area_points', JSON.stringify(args.points), PropertyValueType.JSON), ...sharedProperties(args.identifier, args.name, args.color)],
  };
}

// ============================================================================
// OPENING (door / window — geometry mirrors AREA)
// ============================================================================

export function buildCreateOpeningParams(args: { identifier: string; points: AnnotationPoint[]; name: string; color: string; kind: OpeningKind; wallSketchEdgeId?: string | null; groupIdentifier?: string | null }): CreateAnnotationParams {
  const initialProperties: InitialProperty[] = [
    prop(args.identifier, 'area_points', JSON.stringify(args.points), PropertyValueType.JSON),
    prop(args.identifier, 'opening_kind', args.kind, PropertyValueType.SELECT),
    ...sharedProperties(args.identifier, args.name, args.color),
  ];
  if (args.wallSketchEdgeId) initialProperties.push(prop(args.identifier, 'opening_skeleton_edge_id', args.wallSketchEdgeId, PropertyValueType.STRING));
  return {
    identifier: args.identifier,
    type: 'OPENING',
    groupIdentifier: args.groupIdentifier,
    initialProperties,
  };
}

// ============================================================================
// OPENING_SKETCH (door / window as a line on a wall-sketch edge)
// ============================================================================

export function buildCreateOpeningSketchParams(args: {
  identifier: string;
  name: string;
  color: string;
  wallEdgeId: string;
  offset: number;
  length: number;
  lowerBound: number;
  height: number;
  inset: number;
  kind: OpeningKind;
  doorType: DoorGlyphType;
  windowType?: WindowGlyphType;
  hingeAtStart: boolean;
  swingPositive: boolean;
  rollable: boolean;
  sourceOpeningId: string;
  groupIdentifier?: string | null;
}): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'OPENING_SKETCH',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'openingsketch_wall_edge_id', args.wallEdgeId, PropertyValueType.STRING),
      prop(args.identifier, 'openingsketch_offset', args.offset, PropertyValueType.FLOAT),
      prop(args.identifier, 'openingsketch_length', args.length, PropertyValueType.FLOAT),
      prop(args.identifier, 'openingsketch_lower_bound', args.lowerBound, PropertyValueType.FLOAT),
      prop(args.identifier, 'openingsketch_height', args.height, PropertyValueType.FLOAT),
      prop(args.identifier, 'openingsketch_inset', args.inset, PropertyValueType.FLOAT),
      prop(args.identifier, 'openingsketch_kind', args.kind, PropertyValueType.SELECT),
      prop(args.identifier, 'openingsketch_door_type', args.doorType, PropertyValueType.SELECT),
      prop(args.identifier, 'openingsketch_window_type', args.windowType ?? STANDARD_WINDOW_TYPE, PropertyValueType.SELECT),
      prop(args.identifier, 'openingsketch_hinge_at_start', args.hingeAtStart, PropertyValueType.BOOLEAN),
      prop(args.identifier, 'openingsketch_swing_positive', args.swingPositive, PropertyValueType.BOOLEAN),
      prop(args.identifier, 'openingsketch_rollable', args.rollable, PropertyValueType.BOOLEAN),
      prop(args.identifier, 'openingsketch_source_opening_id', args.sourceOpeningId, PropertyValueType.STRING),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

// ============================================================================
// SKETCH_OBJECT (furniture / fixture symbol in the 2D floor layout)
// ============================================================================

export function buildCreateSketchObjectParams(args: {
  identifier: string;
  name: string;
  color: string;
  typeId: string;
  width: number;
  depth: number;
  anchor: Coordinate[] | null;
  rotation: number;
  wall: { edgeId: string; offset: number; side: boolean; inset: number } | null;
  paramValues: Array<{ identifier: string; value: number | string | boolean; valueType: PropertyValueType }>;
  groupIdentifier?: string | null;
  /** Source STRUCTURE id when converted from a floorplan structure. */
  sourceStructureId?: string;
}): CreateAnnotationParams {
  const initialProperties: InitialProperty[] = [
    prop(args.identifier, 'sketchobject_type_id', args.typeId, PropertyValueType.STRING),
    prop(args.identifier, 'sketchobject_width', args.width, PropertyValueType.FLOAT),
    prop(args.identifier, 'sketchobject_depth', args.depth, PropertyValueType.FLOAT),
    prop(args.identifier, 'sketchobject_anchor', encodePolygon(args.anchor ?? []), PropertyValueType.FLOAT_ARRAY),
    prop(args.identifier, 'sketchobject_rotation', args.rotation, PropertyValueType.FLOAT),
    ...sharedProperties(args.identifier, args.name, args.color),
  ];
  if (args.wall) {
    initialProperties.push(
      prop(args.identifier, 'sketchobject_wall_edge_id', args.wall.edgeId, PropertyValueType.STRING),
      prop(args.identifier, 'sketchobject_wall_offset', args.wall.offset, PropertyValueType.FLOAT),
      prop(args.identifier, 'sketchobject_wall_side', args.wall.side, PropertyValueType.BOOLEAN),
      prop(args.identifier, 'sketchobject_wall_inset', args.wall.inset, PropertyValueType.FLOAT),
    );
  }
  if (args.sourceStructureId) {
    initialProperties.push(prop(args.identifier, 'sketchobject_source_structure_id', args.sourceStructureId, PropertyValueType.STRING));
  }
  for (const param of args.paramValues) {
    initialProperties.push(prop(args.identifier, param.identifier, param.value, param.valueType));
  }
  return {
    identifier: args.identifier,
    type: 'SKETCH_OBJECT',
    groupIdentifier: args.groupIdentifier,
    initialProperties,
  };
}

// ============================================================================
// POINTCLUSTER
// ============================================================================

type ClusterSegment = { pointcloudId: string; segmentId: number };

export function buildCreatePointClusterParams(args: { identifier: string; segments: ClusterSegment[]; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'POINTCLUSTER',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [prop(args.identifier, 'pointcluster_segments', JSON.stringify(args.segments), PropertyValueType.JSON), ...sharedProperties(args.identifier, args.name, args.color)],
  };
}

// ============================================================================
// ROOM
// ============================================================================

type RoomPlane = { position: Coordinate; normal: Coordinate };

function encodePolygon(coords: Coordinate[]): number[] {
  const arr = new Array(coords.length * 3);
  for (let i = 0; i < coords.length; i++) {
    arr[i * 3] = coords[i].x;
    arr[i * 3 + 1] = coords[i].y;
    arr[i * 3 + 2] = coords[i].z;
  }
  return arr;
}

function encodePlane(plane: RoomPlane): number[] {
  return [plane.position.x, plane.position.y, plane.position.z, plane.normal.x, plane.normal.y, plane.normal.z];
}

export function buildCreateRoomParams(args: { identifier: string; polygon: Coordinate[]; base: RoomPlane; ceiling: RoomPlane; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'ROOM',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'room_polygon', encodePolygon(args.polygon), PropertyValueType.FLOAT_ARRAY),
      prop(args.identifier, 'room_base', encodePlane(args.base), PropertyValueType.FLOAT_ARRAY),
      prop(args.identifier, 'room_ceiling', encodePlane(args.ceiling), PropertyValueType.FLOAT_ARRAY),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

// ============================================================================
// WALL_SKETCH
// ============================================================================

// Floor height is not persisted on the wall sketch — it's inherited from the
// sketch's floor group (see PointoramaWallSketch.getFloorHeight).
export function buildCreateWallSketchParams(args: { identifier: string; nodes: unknown[]; edges: unknown[]; metadata?: unknown; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'WALL_SKETCH',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'skeleton_nodes', JSON.stringify(args.nodes), PropertyValueType.JSON),
      prop(args.identifier, 'skeleton_edges', JSON.stringify(args.edges), PropertyValueType.JSON),
      ...(args.metadata != null ? [prop(args.identifier, 'skeleton_metadata', JSON.stringify(args.metadata), PropertyValueType.JSON)] : []),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

// ============================================================================
// OUTLINE
// ============================================================================

// ============================================================================
// STRUCTURE (beam / column / custom)
// ============================================================================

export function buildCreateStructureParams(args: {
  identifier: string;
  polygon: Coordinate[];
  height: number;
  kind: StructureKind;
  roomId?: string | null;
  name: string;
  color: string;
  groupIdentifier?: string | null;
}): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'STRUCTURE',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'structure_polygon', encodePolygon(args.polygon), PropertyValueType.FLOAT_ARRAY),
      prop(args.identifier, 'structure_height', args.height, PropertyValueType.FLOAT),
      prop(args.identifier, 'structure_kind', args.kind, PropertyValueType.SELECT),
      prop(args.identifier, 'structure_room_id', args.roomId ?? '', PropertyValueType.STRING),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}

export function buildCreateOutlineParams(args: { identifier: string; outerRing: Coordinate[]; cavities: Coordinate[][]; floorHeight: number; name: string; color: string; groupIdentifier?: string | null }): CreateAnnotationParams {
  return {
    identifier: args.identifier,
    type: 'OUTLINE',
    groupIdentifier: args.groupIdentifier,
    initialProperties: [
      prop(args.identifier, 'outline_outer_ring', encodePolygon(args.outerRing), PropertyValueType.FLOAT_ARRAY),
      prop(args.identifier, 'outline_cavities', JSON.stringify(args.cavities), PropertyValueType.JSON),
      prop(args.identifier, 'outline_floor_height', args.floorHeight, PropertyValueType.FLOAT),
      ...sharedProperties(args.identifier, args.name, args.color),
    ],
  };
}
